import React, { useEffect, useState } from 'react';
import { getUserLicenses } from '../services/licenseService';
import { License } from '../types/License';
import { CalendarDays, ShoppingCart, Clock, Check, X, AlertTriangle, Download } from 'lucide-react';
import { toast } from '../components/ui/Toast';
import Button from '../components/ui/Button';

const Dashboard: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLicenses();
  }, []);

  const fetchLicenses = async () => {
    try {
      const userLicenses = await getUserLicenses();
      setLicenses(userLicenses);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch license data. Please try again.",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const hasActiveLicense = licenses.some(license =>
    license.status === 'active' && new Date(license.expiresAt) > new Date()
  );

  const getStatusBadge = (status: License['status'], expiresAt: string) => {
    const isExpired = new Date(expiresAt) < new Date();

    if (status === 'active' && !isExpired) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900 text-green-300">
          <Check className="mr-1 w-3 h-3" />
          Active
        </span>
      );
    } else if (status === 'active' || status === 'expired' || isExpired) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-dark-700 text-dark-300">
          <Clock className="mr-1 w-3 h-3" />
          Expired
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900 text-red-300">
          <X className="mr-1 w-3 h-3" />
          Revoked
        </span>
      );
    }
  };

  const handleDownload = () => {
    // Replace with your actual download URL
    window.location.href = 'https://winmaxoptimizer.com/download';
  };

  const handleBuy = () => {
    // Replace with your actual download URL
    window.location.href = 'https://winmaxoptimizer.com/product/winmax-custom-duration';
  };

  return (
    <div className="animate-fadeIn">
      <div className="overflow-hidden mb-8 rounded-lg border shadow-lg bg-dark-800 border-dark-700">
        <div className="px-6 py-5 border-b border-dark-700">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium leading-6 text-dark-100">Your License</h3>
              <p className="mt-1 text-sm text-dark-400">
                View and manage your WinMax optimization software license, including key, issued date, and expiration date.
              </p>
            </div>
            {hasActiveLicense ? (
              <div className="flex gap-2 items-center">
                <Button
                  variant="secondary"
                  icon={ShoppingCart}
                  onClick={handleBuy}
                >
                  Purchase license
                </Button>
                <Button
                  variant="primary"
                  icon={Download}
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </div>

            ) : (
              <Button
                variant="primary"
                icon={ShoppingCart}
                onClick={handleBuy}
              >
                Purchase license
              </Button>
            )}
          </div>
        </div>
        <div className="px-6 py-5">
          {loading ? (
            <div className="py-10 text-center">
              <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-primary-500"></div>
              <p className="mt-2 text-sm text-dark-400">Loading license data...</p>
            </div>
          ) : licenses.length > 0 ? (
            <div className="overflow-hidden">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {licenses.map((license) => (
                  <div
                    key={license.id}
                    className="overflow-hidden rounded-lg border shadow-lg transition-shadow duration-300 bg-dark-800 border-dark-700 hover:shadow-xl"
                  >
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex justify-between items-start">
                        <div className="font-mono text-sm bg-dark-700 rounded px-3 py-1 text-dark-200 select-all overflow-hidden text-ellipsis max-w-[180px]">
                          {license.key.substring(0, 14)}...
                        </div>
                        {getStatusBadge(license.status, license.expiresAt)}
                      </div>

                      <div className="mt-4 space-y-3">
                        <div className="flex items-center text-sm">
                          <CalendarDays className="flex-shrink-0 mr-1.5 h-4 w-4 text-dark-400" />
                          <span className="mr-1 text-dark-400">Issued:</span>
                          <span className="font-medium text-dark-200">
                            {formatDate(license.issuedAt)}
                          </span>
                        </div>

                        <div className="flex items-center text-sm">
                          <Clock className="flex-shrink-0 mr-1.5 h-4 w-4 text-dark-400" />
                          <span className="mr-1 text-dark-400">Expires:</span>
                          <span className="font-medium text-dark-200">
                            {formatDate(license.expiresAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-6 text-center rounded-lg border bg-yellow-900/20 border-yellow-900/50">
              <AlertTriangle className="mx-auto mb-2 w-8 h-8 text-yellow-600" />
              <h3 className="mb-1 text-lg font-medium text-yellow-500">No licenses found</h3>
              <p className="text-sm text-yellow-600">
                You don't have any WinMax licenses associated with your account.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;